import React, { useEffect, useLayoutEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import {
  authenticationService,
  connectionService,
  invitationService,
  profileService,
  communityService,
} from '../../services';
import {
  CustomFieldResponses,
  CustomFieldType,
  InviteType,
  NetworkInviteLinkModel,
  NetworkInviteModel,
} from '../../types/invites';
import { ProfileDataType, ProfileField } from '../../types/profile';
import { createEmptyCustomFieldResponses, createEmptyField } from '../../utils/formatUtils';
import { sortProfileFields } from '../../utils/sortUtils';
import { checkMandatoryFields, isEmptyField, isValidProfileFields } from '../../utils/validationUtils';
import ProfileFieldForm from '../../components/forms/profile/ProfileFieldForm';
import CustomFieldsForm from '../../components/forms/CustomFieldsForm';
import { LoginPageState, SuccessPageState } from '../../types/navigation';
import NameInput from '../../components/forms/profile/NameInput';
import TermsAndConditions from '../../components/misc/TermsAndConditions';
import ResponsePageWrapper from '../../components/layouts/ResponsePageWrapper';
import EmailInput from '../../components/forms/profile/EmailInput';
import CommunicationNameInput from '../../components/forms/profile/CommunicationNameInput';
import { UserAccountCreationChannel, UserAccountCreationEvent } from '../../analytics/models';
import { SharedBusinessData } from '../../types/misc';
import useMagicLogin from '../../hooks/useMagicLogin';
import useDeleteParams from '../../hooks/useDeleteParams';
import { CommunityOverview } from '../../components/misc/CommunityOverview';
import StepFlow, { StepFlowStep, StepIndicator } from '../../components/layouts/StepFlow';
import AccountExistsModal from '../../components/modals/AccountExistsModal';
import { MagicGenerationRequest } from '../../types/requests';

export default function UnauthenticatedBusinessInvitePage(): JSX.Element {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const invitationId = params.get('invitationId');
  const link = params.get('link');

  if (useMagicLogin()) {
    return <></>;
  }

  useDeleteParams('link');

  const [invite, setInvite] = useState<NetworkInviteModel | NetworkInviteLinkModel>();
  const [alias, setAlias] = useState<string>('');
  const [profileFields, setProfileFields] = useState<ProfileField[]>([]);
  const [customFieldResponses, setCustomFieldResponses] = useState<CustomFieldResponses>(
    createEmptyCustomFieldResponses([]),
  );
  const [terms, setTerms] = useState<boolean>(false);
  const [validateStep1, setValidateStep1] = useState<boolean>(false);
  const [validateStep2, setValidateStep2] = useState<boolean>(false);
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [step1Errors, setStep1Errors] = useState<string[]>([]);
  const [step2Errors, setStep2Errors] = useState<string[]>([]);
  const [showAccountExistsModal, setShowAccountExistsModal] = useState<boolean>(false);
  const [magicLinkRequest, setMagicLinkRequest] = useState<MagicGenerationRequest>();
  const [isEmailSelected, setIsEmailSelected] = useState<boolean>(true);

  useEffect(() => {
    if (!invitationId) {
      toast.error(t('toast.error.general.invalidInvite'));
      navigate('/');
    }
  }, []);

  useLayoutEffect(() => {
    if (!invitationId || link) return;

    const setFields = async (): Promise<void> => {
      const { networkInvite }: LoginPageState = location.state ?? { networkInvite: undefined };

      const inv =
        networkInvite ||
        (await invitationService.getNetworkInvite(invitationId).catch(() => {
          toast.error(t('toast.error.general.inviteNotFound'));
          navigate('/');
        }));

      if (!inv) return;
      setInvite(inv as NetworkInviteModel | NetworkInviteLinkModel);
      const { mandatoryFields, optionalFields, customFields } = inv;
      const dataRequest = Array.from(
        new Set([...(mandatoryFields ?? []), ...(optionalFields ?? []), ProfileDataType.EMAIL]),
      );
      if (!inv.dashboardFeatures?.MEMBER_ADMINISTRATION && inv.communityFeatures?.EASY_SHARING) {
        dataRequest.push(ProfileDataType.ADDRESS);
        dataRequest.push(ProfileDataType.PHONENUMBER);
        dataRequest.push(ProfileDataType.BIRTHDATE);
      }
      setProfileFields(sortProfileFields(dataRequest.map((dr) => createEmptyField(dr))));
      setCustomFieldResponses(createEmptyCustomFieldResponses(customFields));
    };

    setFields();
  }, []);

  const validateStep1Fields = async (): Promise<boolean> => {
    const errors: string[] = [];

    if (!alias || !alias.split('+')[0] || !alias.split('+')[1]) {
      errors.push(t('toast.error.field.name'));
    }
    if (!terms) {
      errors.push(t('toast.error.general.terms'));
    }

    const emailField = profileFields.find((x) => x.dataType === ProfileDataType.EMAIL);

    if (isEmptyField(emailField!)) {
      errors.push(t('toast.error.field.email'));
    } else {
      const email = emailField!.email!;
      if (!email) {
        errors.push(t('toast.error.field.email'));
      } else if (!isValidProfileFields([emailField!])) {
        errors.push(t('toast.error.field.email'));
      } else {
        try {
          const exists = await profileService.checkEmailExists(email);
          if (exists) {
            const request: MagicGenerationRequest = {
              email,
              invitationId: invitationId || undefined,
              inviteType: InviteType.BUSINESS,
            };
            setMagicLinkRequest(request);
            setShowAccountExistsModal(true);
            return false;
          }
        } catch (error) {
          errors.push(t('toast.error.field.email'));
        }
      }
    }

    setStep1Errors(errors);
    return errors.length === 0;
  };

  const handleStepChange = async (direction: 1 | -1) => {
    if (direction === 1 && currentStep === 0) {
      setValidateStep1(true);
      const isValid = await validateStep1Fields();
      if (!isValid) return;
    }

    setCurrentStep((prev) => {
      const nextStep = prev + direction;
      const maxStep = invite?.dashboardFeatures?.MEMBER_ADMINISTRATION ? 1 : 0;
      return Math.max(0, Math.min(nextStep, maxStep));
    });
  };

  const handleSave = async () => {
    if (!invite || !invitationId) return;

    // If there is no data step (no data shared with admins), validate the
    // account step fields here before proceeding.
    if (!invite.dashboardFeatures?.MEMBER_ADMINISTRATION) {
      setValidateStep1(true);
      const isStep1Valid = await validateStep1Fields();
      if (!isStep1Valid) return;
    }

    setValidateStep2(true);
    const errors: string[] = [];
    if (!checkMandatoryFields(invite!, profileFields, customFieldResponses)) {
      errors.push(t('toast.error.general.mandatoryFields'));
    }

    setStep2Errors(errors);
    if (errors.length > 0) return;
    const email = profileFields.find((x) => x.dataType === ProfileDataType.EMAIL)!.email!;

    const event: UserAccountCreationEvent = {
      channel: UserAccountCreationChannel[UserAccountCreationChannel.DEFAULT],
      details: undefined,
    };

    const login = await authenticationService.register(alias, email, event);
    const properties = await profileService.addFields(
      profileFields.filter((x) => !isEmptyField(x) && x.dataType !== ProfileDataType.EMAIL),
    );

    const userProfileData = await profileService.getUserData();
    const userEmailField = userProfileData.find((f) => f.dataType === ProfileDataType.EMAIL);

    const args: SharedBusinessData = {
      propertyIds: invite.dashboardFeatures?.MEMBER_ADMINISTRATION
        ? properties
            .filter((f) => f.dataType !== ProfileDataType.EMAIL || !invite.nonRequestedFields?.includes(f.dataType))
            .map((x) => x.id!)
            .concat(
              userEmailField?.id &&
                isEmailSelected &&
                !invite.nonRequestedFields?.includes(ProfileDataType.EMAIL) &&
                !properties.some(
                  (p) => p.dataType === ProfileDataType.EMAIL && !invite.nonRequestedFields?.includes(p.dataType),
                )
                ? [userEmailField.id]
                : [],
            )
        : [],
      customFieldResponses,
      dataForMembers: [],
    };

    await connectionService.createBusinessConnection(invitationId, args);

    if (
      !invite.dashboardFeatures?.MEMBER_ADMINISTRATION &&
      invite.communityId &&
      invite.communityFeatures?.EASY_SHARING
    ) {
      const memberPropertyIds = properties.map((d) => +d.id!);

      if (userEmailField?.id && isEmailSelected && !memberPropertyIds.includes(+userEmailField.id)) {
        memberPropertyIds.push(+userEmailField.id);
      }

      if (memberPropertyIds.length > 0) {
        await connectionService.updateDataSharedWithCommunityMembers(invite.communityId, memberPropertyIds);
      }
    }

    const state: SuccessPageState = {
      translationKey: `page.common.success.message.${
        invite?.communityId ? 'businessCommunityInvite' : 'businessInvite'
      }`,
      translationOptions: {
        businessName: invite?.name,
      },
      networkInvite: invite,
    };
    toast.success(t('toast.success.connectionCreated'));
    if (invite.communityId && invite.communityFeatures?.EASY_SHARING)
      navigate(`/communities/${invite.communityId}/members`, { state: { invite, success: state, replace: true } });
    else navigate('/success', { state });
    setTimeout(async () => {
      login();
      try {
        await communityService.fetchCommunities();
      } catch (error) {
        // ignore - will refresh on next load
      }
    }, 1);
  };

  const setProfileFieldByType = (field: ProfileField) => {
    if (field.dataType === ProfileDataType.SOCIAL) {
      setProfileFields(
        profileFields.map(
          (f: ProfileField): ProfileField => (f.social?.socialType === field.social?.socialType ? field : f),
        ),
      );
    } else
      setProfileFields(
        profileFields.map((f: ProfileField): ProfileField => (f.dataType === field.dataType ? field : f)),
      );
  };

  const showDataStep = invite?.dashboardFeatures?.MEMBER_ADMINISTRATION;

  const steps = [
    <AccountStep
      alias={alias}
      setAlias={setAlias}
      terms={terms}
      setTerms={setTerms}
      profileFields={profileFields}
      customFieldResponses={customFieldResponses}
      setCustomFieldResponses={setCustomFieldResponses}
      validateStep1={validateStep1}
      setProfileFieldByType={setProfileFieldByType}
      handleStepChange={handleStepChange}
      invite={invite}
      invitationId={invitationId}
      handleSave={handleSave}
      errors={step1Errors}
      setErrors={setStep1Errors}
    />,
    ...(showDataStep
      ? [
          <DataStep
            invite={invite}
            profileFields={profileFields}
            customFieldResponses={customFieldResponses}
            setCustomFieldResponses={setCustomFieldResponses}
            setProfileFieldByType={setProfileFieldByType}
            handleStepChange={handleStepChange}
            requesterName={invite?.name}
            handleSave={handleSave}
            validateStep2={validateStep2}
            errors={step2Errors}
            setErrors={setStep2Errors}
            isEmailSelected={isEmailSelected}
            setIsEmailSelected={setIsEmailSelected}
          />,
        ]
      : []),
  ];

  return (
    <>
      <AccountExistsModal
        open={showAccountExistsModal}
        setOpen={setShowAccountExistsModal}
        request={magicLinkRequest}
      />
      <ResponsePageWrapper
        stepIndicator={steps.length > 1 && <StepIndicator indexState={[currentStep, setCurrentStep]} steps={steps} />}>
        {invite && (
          <ResponsePageWrapper.SidePanel>
            <CommunityOverview invite={invite} />
          </ResponsePageWrapper.SidePanel>
        )}

        <StepFlow indexState={[currentStep, setCurrentStep]} steps={steps} hideIndicators />
      </ResponsePageWrapper>
    </>
  );
}

interface AccountStepProps {
  alias: string;
  setAlias: (alias: string) => void;
  terms: boolean;
  setTerms: (terms: boolean) => void;
  profileFields: ProfileField[];
  customFieldResponses: CustomFieldResponses;
  setCustomFieldResponses: (responses: CustomFieldResponses) => void;
  validateStep1: boolean;
  handleSave: () => Promise<void>;
  setProfileFieldByType: (field: ProfileField) => void;
  handleStepChange: (direction: 1 | -1) => void;
  invite: NetworkInviteModel | NetworkInviteLinkModel | undefined;
  invitationId: string | null;
  errors: string[];
  setErrors: (errors: string[]) => void;
}

function AccountStep({
  alias,
  setAlias,
  terms,
  setTerms,
  profileFields,
  customFieldResponses,
  setCustomFieldResponses,
  validateStep1,
  handleSave,
  setProfileFieldByType,
  handleStepChange,
  invite,
  invitationId,
  errors,
  setErrors,
}: AccountStepProps): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [accountExists, setAccountExists] = useState<boolean>(false);

  const emailField = profileFields.find((x) => x.dataType === ProfileDataType.EMAIL);
  const communicationName = invite?.customFields?.find((f) => f.type === CustomFieldType.COMMUNICATION_NAME);

  // Clear errors when user starts typing/interacting
  useEffect(() => {
    if (errors.length > 0) {
      setErrors([]);
    }
  }, [alias, terms, profileFields, customFieldResponses]);

  const handleLogin = async () => {
    const state: LoginPageState = {
      email: profileFields.find((x) => x.dataType === ProfileDataType.EMAIL)!.email!,
      invitationId: invitationId!,
      networkInvite: invite!,
      inviteType: InviteType.BUSINESS,
    };
    navigate('/login', { state });
  };

  const showDataStep = invite?.dashboardFeatures?.MEMBER_ADMINISTRATION;

  const handleSubmit = () => {
    if (showDataStep) {
      handleStepChange(1);
    } else {
      handleSave();
    }
  };

  return (
    <StepFlowStep
      setStep={handleStepChange}
      showPrev={false}
      onSubmit={handleSubmit}
      showNext={!!showDataStep}
      submitText={t('page.shared.acceptInvite')}
      title={t('page.businessInvite.createAccount')}>
      <div className="flex flex-col gap-4">
        {errors.length > 0 && (
          <div className="rounded-lg border border-error bg-error/10 p-3">
            {errors.map((error, index) => (
              <p key={index} className="text-sm text-error">
                {error}
              </p>
            ))}
          </div>
        )}
        <NameInput alias={alias} setAlias={setAlias} mandatory validate={validateStep1} />

        {communicationName && (
          <CommunicationNameInput
            name={customFieldResponses[communicationName.customFieldId]}
            validate={validateStep1}
            setName={(name) =>
              setCustomFieldResponses({
                ...customFieldResponses,
                [communicationName.customFieldId]: name,
              })
            }
            mandatory={communicationName?.mandatory}
          />
        )}

        {emailField && (
          <EmailInput
            field={emailField!}
            emailCheck
            validate={validateStep1}
            mandatory
            accountExists={accountExists}
            setAccountExists={setAccountExists}
            setField={setProfileFieldByType}
          />
        )}
        <span className="text-right text-sm text-primary">
          {t('page.shared.alreadyHaveAccount')}{' '}
          <strong className="cursor-pointer underline" onClick={handleLogin}>
            {t('page.shared.logInHere')}
          </strong>
          .
        </span>

        <TermsAndConditions checked={terms} setChecked={setTerms} />
      </div>
    </StepFlowStep>
  );
}

interface DataStepProps {
  invite: NetworkInviteModel | NetworkInviteLinkModel | undefined;
  profileFields: ProfileField[];
  customFieldResponses: CustomFieldResponses;
  setCustomFieldResponses: (responses: CustomFieldResponses) => void;
  setProfileFieldByType: (field: ProfileField) => void;
  handleStepChange: (direction: 1 | -1) => void;
  requesterName: string;
  handleSave: () => Promise<void>;
  validateStep2: boolean;
  errors: string[];
  setErrors: (errors: string[]) => void;
  isEmailSelected: boolean;
  setIsEmailSelected: (selected: boolean) => void;
}

function DataStep({
  invite,
  profileFields,
  customFieldResponses,
  setCustomFieldResponses,
  setProfileFieldByType,
  handleStepChange,
  requesterName,
  handleSave,
  validateStep2,
  errors,
  setErrors,
  isEmailSelected,
  setIsEmailSelected,
}: DataStepProps): JSX.Element {
  const { t } = useTranslation();

  // Store the original email to preserve it when toggling
  const [originalEmail, setOriginalEmail] = useState<string>('');

  useEffect(() => {
    const emailField = profileFields.find((f) => f.dataType === ProfileDataType.EMAIL);
    if (emailField?.email && !originalEmail) {
      setOriginalEmail(emailField.email);
    }
  }, [profileFields]);

  useEffect(() => {
    if (errors.length > 0) {
      setErrors([]);
    }
  }, [profileFields, customFieldResponses]);

  const handleSaveData = async () => {
    await handleSave();
  };

  const onlyEmail =
    (invite?.mandatoryFields?.length === 1 &&
      invite?.optionalFields?.length === 0 &&
      invite?.mandatoryFields[0] === ProfileDataType.EMAIL) ||
    (invite?.mandatoryFields?.length === 0 &&
      invite?.optionalFields?.length === 1 &&
      invite?.optionalFields[0] === ProfileDataType.EMAIL);

  return (
    <StepFlowStep
      setStep={handleStepChange}
      onSubmit={handleSaveData}
      showNext={false}
      title={t('page.businessInvite.description', { communityName: invite?.name })}
      submitText={t('page.shared.acceptInvite')}>
      <div className="flex flex-col gap-4">
        {errors.length > 0 && (
          <div className="rounded-lg border border-error bg-error/10 p-3">
            {errors.map((error, index) => (
              <p key={index} className="text-sm text-error">
                {error}
              </p>
            ))}
          </div>
        )}
        <div className="flex flex-row items-start">
          <img src="/images/misc/shield.svg" alt="Onboarding visual" className="mr-2 mt-[3px] h-5 w-5 object-cover" />
          <p>
            {invite?.dashboardFeatures?.MEMBER_ADMINISTRATION
              ? onlyEmail
                ? t('page.businessInvite.withAdminOnlyEmail', { name: requesterName })
                : t('page.businessInvite.withAdmin', { name: requesterName })
              : t('page.businessInvite.noAdmin', { name: requesterName })}
          </p>
        </div>

        {profileFields.map((field) => {
          if (!invite?.mandatoryFields?.includes(field.dataType) && !invite?.optionalFields?.includes(field.dataType)) {
            return null;
          }

          if (field.dataType === ProfileDataType.EMAIL) {
            const isMandatory = invite?.mandatoryFields?.includes(ProfileDataType.EMAIL);

            return (
              <div key={field.dataType} className="relative flex w-full flex-col">
                <label className="mb-1 pr-2 font-medium">
                  {t('form.email.email')} {isMandatory ? '*' : ''}
                </label>
                <div
                  onClick={() => {
                    if (!isMandatory) {
                      setIsEmailSelected(!isEmailSelected);
                    }
                  }}
                  className={`flex w-full ${
                    isMandatory ? 'cursor-not-allowed' : 'cursor-pointer'
                  } items-center justify-between rounded-lg border bg-secondary px-3 py-2 ${
                    isEmailSelected && !isMandatory ? 'bg-secondary-200' : ''
                  }`}>
                  <label className="text-sm font-medium text-primary-900">
                    {originalEmail || field.email || t('form.email.email')}
                  </label>
                  {!isMandatory && (
                    <div className="flex h-8 w-8 items-center justify-center">
                      <input
                        type="checkbox"
                        className="form-checkbox h-5 w-5 cursor-pointer rounded-full border-none bg-secondary-200 text-primary-300 accent-primary-900 focus:ring-0 focus:ring-transparent"
                        checked={isEmailSelected}
                        readOnly
                      />
                    </div>
                  )}
                </div>
              </div>
            );
          }

          return (
            <ProfileFieldForm
              key={field.dataType}
              field={field}
              canBeEmpty={invite?.optionalFields?.includes(field.dataType)}
              validate={validateStep2}
              mandatory={invite?.mandatoryFields?.includes(field.dataType)}
              setField={setProfileFieldByType}
            />
          );
        })}
        <CustomFieldsForm
          fields={invite?.customFields || []}
          response={customFieldResponses}
          setResponse={setCustomFieldResponses}
        />
      </div>
    </StepFlowStep>
  );
}
